import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { getPracticePlanStatus, hasVisiblePlanContent, planTemplateToForm } from '../utils/practicePlan';
import './PracticePlanSection.css';

const FOCUS_AREAS = [
  { value: '', label: 'Select focus' },
  { value: 'PASSING', label: 'Passing' },
  { value: 'SHOOTING', label: 'Shooting' },
  { value: 'DEFENDING', label: 'Defending' },
  { value: 'FITNESS', label: 'Fitness' },
  { value: 'SET_PIECES', label: 'Set pieces' },
  { value: 'GOALKEEPING', label: 'Goalkeeping' },
  { value: 'OTHER', label: 'Other' },
];

const formatFocusArea = (value) => {
  const match = FOCUS_AREAS.find((f) => f.value === value);
  return match?.label || value?.replace(/_/g, ' ') || '';
};

const emptyForm = () => ({
  objectives: [''],
  warmup: { durationMinutes: '', description: '' },
  drills: [{ name: '', durationMinutes: '', description: '', focusArea: '' }],
  playerNotes: '',
});

const planToForm = (plan) => {
  if (!plan) return emptyForm();
  return {
    objectives: plan.objectives?.length ? [...plan.objectives] : [''],
    warmup: {
      durationMinutes: plan.warmup?.durationMinutes ?? '',
      description: plan.warmup?.description || '',
    },
    drills: plan.drills?.length
      ? plan.drills.map((d) => ({
          name: d.name || '',
          durationMinutes: d.durationMinutes ?? '',
          description: d.description || '',
          focusArea: d.focusArea || '',
        }))
      : [{ name: '', durationMinutes: '', description: '', focusArea: '' }],
    playerNotes: plan.playerNotes || '',
  };
};

const formToRequest = (form, publish = false) => ({
  objectives: form.objectives.map((o) => o.trim()).filter(Boolean),
  warmup: {
    durationMinutes: form.warmup.durationMinutes ? Number(form.warmup.durationMinutes) : null,
    description: form.warmup.description.trim() || null,
  },
  drills: form.drills
    .filter((d) => d.name.trim())
    .map((d) => ({
      name: d.name.trim(),
      durationMinutes: d.durationMinutes ? Number(d.durationMinutes) : null,
      description: d.description.trim() || null,
      focusArea: d.focusArea || null,
    })),
  playerNotes: form.playerNotes.trim() || null,
  publish,
});

const PracticePlanSection = ({ practiceId, practice, practicePlan, practicePlanStatus, canEdit, userId, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const status = practicePlanStatus || getPracticePlanStatus(practice);
  const isDraft = status === 'DRAFT';
  const isPublished = status === 'PUBLISHED';

  useEffect(() => {
    setForm(planToForm(practicePlan));
  }, [practicePlan]);

  const handleSave = useCallback(async (publish = false) => {
    if (!userId || !practiceId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const updated = await api.updatePracticePlan(practiceId, formToRequest(form, publish), userId);
      setEditing(false);
      setSuccess(publish ? 'Practice plan published' : 'Practice plan saved as draft');
      onUpdated?.(updated);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save practice plan');
    } finally {
      setLoading(false);
    }
  }, [practiceId, form, userId, onUpdated]);

  const handlePublish = useCallback(async () => {
    if (!userId || !practiceId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const updated = await api.publishPracticePlan(practiceId, userId);
      setEditing(false);
      setSuccess('Practice plan published');
      onUpdated?.(updated);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to publish practice plan');
    } finally {
      setLoading(false);
    }
  }, [practiceId, userId, onUpdated]);

  const handleDuplicateFromLast = useCallback(async () => {
    if (!userId || !practiceId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const template = await api.getPracticePlanTemplate(practiceId, userId);
      const duplicated = planTemplateToForm(template);
      if (duplicated) {
        setForm(duplicated);
        setEditing(true);
        setSuccess('Loaded plan from last published practice');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'No previous published plan found');
    } finally {
      setLoading(false);
    }
  }, [practiceId, userId]);

  const updateObjective = (index, value) => {
    setForm((prev) => {
      const objectives = [...prev.objectives];
      objectives[index] = value;
      return { ...prev, objectives };
    });
  };

  const addObjective = () => {
    setForm((prev) => ({ ...prev, objectives: [...prev.objectives, ''] }));
  };

  const removeObjective = (index) => {
    setForm((prev) => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index),
    }));
  };

  const updateDrill = (index, field, value) => {
    setForm((prev) => {
      const drills = [...prev.drills];
      drills[index] = { ...drills[index], [field]: value };
      return { ...prev, drills };
    });
  };

  const addDrill = () => {
    setForm((prev) => ({
      ...prev,
      drills: [...prev.drills, { name: '', durationMinutes: '', description: '', focusArea: '' }],
    }));
  };

  const removeDrill = (index) => {
    setForm((prev) => ({
      ...prev,
      drills: prev.drills.filter((_, i) => i !== index),
    }));
  };

  const startEditing = () => {
    setForm(planToForm(practicePlan));
    setEditing(true);
    setError('');
    setSuccess('');
  };

  const cancelEditing = () => {
    setForm(planToForm(practicePlan));
    setEditing(false);
    setError('');
  };

  const showContent = hasVisiblePlanContent(practicePlan);

  return (
    <section className="practice-plan-section" id="practice-plan">
      <div className="practice-plan-section__header">
        <div className="practice-plan-section__title-row">
          <h3>Practice plan</h3>
          {canEdit && isDraft && showContent && (
            <span className="practice-plan-badge practice-plan-badge--draft">Draft</span>
          )}
          {isPublished && (
            <span className="practice-plan-badge practice-plan-badge--ready">Published</span>
          )}
        </div>
        {canEdit && !editing && (
          <div className="practice-plan-section__header-actions">
            <button type="button" className="btn btn-sm btn-secondary" onClick={handleDuplicateFromLast} disabled={loading}>
              Duplicate from last
            </button>
            <button type="button" className="btn btn-sm btn-secondary" onClick={startEditing}>
              {showContent ? 'Edit plan' : 'Add plan'}
            </button>
          </div>
        )}
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {!editing && !showContent && (
        <p className="practice-plan-section__empty">
          {canEdit
            ? 'No practice guidance yet. Add warm-up notes, drills, and session goals, or duplicate from the last published practice.'
            : 'Practice guidance will appear here once your specialist publishes a plan.'}
        </p>
      )}

      {!editing && showContent && (
        <div className="practice-plan-section__view">
          {practicePlan.objectives?.length > 0 && (
            <div className="practice-plan-block">
              <h4>Session goals</h4>
              <ul>
                {practicePlan.objectives.map((goal, i) => (
                  <li key={i}>{goal}</li>
                ))}
              </ul>
            </div>
          )}

          {practicePlan.warmup?.description && (
            <div className="practice-plan-block">
              <h4>
                Warm-up
                {practicePlan.warmup.durationMinutes
                  ? ` (${practicePlan.warmup.durationMinutes} min)`
                  : ''}
              </h4>
              <p>{practicePlan.warmup.description}</p>
            </div>
          )}

          {practicePlan.drills?.length > 0 && (
            <div className="practice-plan-block">
              <h4>Drills</h4>
              <div className="practice-plan-drills">
                {practicePlan.drills.map((drill, i) => (
                  <div key={i} className="practice-plan-drill-card">
                    <div className="practice-plan-drill-card__title">
                      <strong>{drill.name}</strong>
                      {drill.durationMinutes ? (
                        <span className="practice-plan-drill-card__duration">{drill.durationMinutes} min</span>
                      ) : null}
                    </div>
                    {drill.focusArea && (
                      <span className="practice-plan-drill-card__focus">{formatFocusArea(drill.focusArea)}</span>
                    )}
                    {drill.description && <p>{drill.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {practicePlan.playerNotes && (
            <div className="practice-plan-block">
              <h4>Notes for players</h4>
              <p>{practicePlan.playerNotes}</p>
            </div>
          )}

          {practicePlan.updatedAt && (
            <p className="practice-plan-section__meta">
              Updated {new Date(practicePlan.updatedAt).toLocaleString()}
              {practicePlan.authorName ? ` by ${practicePlan.authorName}` : ''}
              {practicePlan.publishedAt && isPublished && (
                <> · Published {new Date(practicePlan.publishedAt).toLocaleString()}</>
              )}
            </p>
          )}
        </div>
      )}

      {editing && (
        <form
          className="practice-plan-section__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave(false);
          }}
        >
          <div className="practice-plan-form-group">
            <label>Session goals</label>
            {form.objectives.map((objective, index) => (
              <div key={index} className="practice-plan-inline-row">
                <input
                  type="text"
                  value={objective}
                  onChange={(e) => updateObjective(index, e.target.value)}
                  placeholder="e.g. Improve first touch under pressure"
                  maxLength={200}
                />
                {form.objectives.length > 1 && (
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => removeObjective(index)}>
                    Remove
                  </button>
                )}
              </div>
            ))}
            {form.objectives.length < 10 && (
              <button type="button" className="btn btn-sm btn-secondary" onClick={addObjective}>
                + Add goal
              </button>
            )}
          </div>

          <div className="practice-plan-form-group">
            <label>Warm-up</label>
            <div className="practice-plan-warmup-grid">
              <input
                type="number"
                min="1"
                max="120"
                value={form.warmup.durationMinutes}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    warmup: { ...prev.warmup, durationMinutes: e.target.value },
                  }))
                }
                placeholder="Minutes"
              />
              <textarea
                value={form.warmup.description}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    warmup: { ...prev.warmup, description: e.target.value },
                  }))
                }
                placeholder="Jog, dynamic stretches, passing rondo..."
                rows={3}
                maxLength={1000}
              />
            </div>
          </div>

          <div className="practice-plan-form-group">
            <label>Drills</label>
            {form.drills.map((drill, index) => (
              <div key={index} className="practice-plan-drill-form">
                <div className="practice-plan-drill-form__row">
                  <input
                    type="text"
                    value={drill.name}
                    onChange={(e) => updateDrill(index, 'name', e.target.value)}
                    placeholder="Drill name"
                    maxLength={100}
                  />
                  <input
                    type="number"
                    min="1"
                    max="120"
                    value={drill.durationMinutes}
                    onChange={(e) => updateDrill(index, 'durationMinutes', e.target.value)}
                    placeholder="Min"
                  />
                  <select
                    value={drill.focusArea}
                    onChange={(e) => updateDrill(index, 'focusArea', e.target.value)}
                  >
                    {FOCUS_AREAS.map((f) => (
                      <option key={f.value || 'none'} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={drill.description}
                  onChange={(e) => updateDrill(index, 'description', e.target.value)}
                  placeholder="How to run the drill, coaching points..."
                  rows={2}
                  maxLength={500}
                />
                {form.drills.length > 1 && (
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => removeDrill(index)}>
                    Remove drill
                  </button>
                )}
              </div>
            ))}
            {form.drills.length < 15 && (
              <button type="button" className="btn btn-sm btn-secondary" onClick={addDrill}>
                + Add drill
              </button>
            )}
          </div>

          <div className="practice-plan-form-group">
            <label htmlFor="player-notes">Notes for players</label>
            <textarea
              id="player-notes"
              value={form.playerNotes}
              onChange={(e) => setForm((prev) => ({ ...prev, playerNotes: e.target.value }))}
              placeholder="What to bring, footwear, homework..."
              rows={3}
              maxLength={1000}
            />
          </div>

          <div className="practice-plan-section__actions">
            <button type="submit" className="btn btn-secondary" disabled={loading}>
              {loading ? 'Saving...' : 'Save draft'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={loading}
              onClick={() => handleSave(true)}
            >
              {loading ? 'Saving...' : 'Save & publish'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={cancelEditing} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {canEdit && !editing && showContent && isDraft && (
        <div className="practice-plan-section__actions" style={{ marginTop: '1rem' }}>
          <button type="button" className="btn btn-primary" onClick={handlePublish} disabled={loading}>
            Publish plan
          </button>
        </div>
      )}
    </section>
  );
};

export default PracticePlanSection;
